import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setSessionLostHandler } from '../lib/api';
import { disconnectSocket } from '../hooks/useLive';

/**
 * Session state for the whole app.
 *
 * The permission list comes from the server (`/api/auth/me`), it is not
 * derived from the role in the browser. That matters: the client and the
 * server would otherwise each hold their own copy of the matrix, and the day
 * they disagree the UI offers an action the API refuses.
 *
 * The UI hides; the API refuses. Hiding is a courtesy so people are not shown
 * doors they cannot open — it is never the control.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | authenticated | anonymous

  const loadSession = useCallback(async () => {
    const me = await api.get('/api/auth/me');
    setSession(me);
    setStatus('authenticated');
    return me;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      disconnectSocket();
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  // On a cold load there is no access token in memory, but there may still be
  // a valid refresh cookie — so try to resume before showing the login screen.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await api.refreshSession();
        if (!cancelled) await loadSession();
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  // When the API gives up on the session, drop straight to anonymous rather
  // than leaving the app rendering against a user that no longer exists.
  useEffect(() => {
    setSessionLostHandler(() => {
      disconnectSocket();
      setSession(null);
      setStatus('anonymous');
    });
    return () => setSessionLostHandler(null);
  }, []);

  const signIn = useCallback(
    async (email, password) => {
      await api.login(email, password);
      return loadSession();
    },
    [loadSession]
  );

  const value = useMemo(() => {
    const permissions = new Set(session?.permissions || []);
    return {
      status,
      user: session?.user || null,
      scope: session?.scope || null,
      historyWindowDays: session?.historyWindowDays ?? null,
      permissions,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      signIn,
      signOut,
      reload: loadSession,
    };
  }, [session, status, signIn, signOut, loadSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** Convenience for a single permission check inside a component. */
export function usePermission(permission) {
  return useAuth().can(permission);
}
